import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ContactInput, JobImportRow } from "@career-ops-amplify/shared";
import { useBatchCreateContacts } from "@/hooks/useContacts";
import { importJobs } from "@/api/jobs";
import { useMutation } from "@tanstack/react-query";
import { cn } from "@/cn";
import { parseCsvLine } from "@/lib/csv";
import {
  applyJobMapping,
  detectJobCsvInput,
} from "@/lib/job-csv-mapping";

type IntakeMode = "jobs" | "contacts";

interface IntakeRow {
  id: string;
  selected: boolean;
  url?: string;
  company: string;
  role?: string;
  name?: string;
  email?: string;
  location?: string;
  notes?: string;
}

function parseJobsCsv(text: string): IntakeRow[] {
  const detected = detectJobCsvInput(text);
  if (detected.rows.length === 0) return [];
  return applyJobMapping(detected.headers, detected.rows, detected.mapping).map(
    (r) => ({
      id: r.id,
      selected: true,
      url: r.url,
      company: r.company || "Unknown",
      role: r.role || "Untitled role",
      location: r.location,
      notes: r.notes,
    }),
  );
}

function parseContactsCsv(text: string): IntakeRow[] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length === 0) return [];

  const header = parseCsvLine(lines[0]).map((h) => h.toLowerCase());
  const rows: IntakeRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const get = (keys: string[]) => {
      for (const key of keys) {
        const idx = header.indexOf(key);
        if (idx >= 0 && cols[idx]) return cols[idx];
      }
      return "";
    };

    const name = get(["name", "contact", "full_name"]);
    const companySlug = get(["company_slug", "company", "employer"]);
    if (!name || !companySlug) continue;
    rows.push({
      id: `row-${i}`,
      selected: true,
      name,
      company: companySlug,
      email: get(["email"]) || undefined,
      role: get(["role", "title"]) || undefined,
      notes: get(["notes"]) || undefined,
    });
  }
  return rows;
}

function parseJson(text: string, mode: IntakeMode): IntakeRow[] {
  const data = JSON.parse(text) as unknown;
  const list = Array.isArray(data)
    ? data
    : typeof data === "object" && data !== null
      ? ((data as Record<string, unknown>).jobs ??
          (data as Record<string, unknown>).contacts ??
          [])
      : [];
  if (!Array.isArray(list)) return [];

  return list.map((item, i) => {
    const row = item as Record<string, unknown>;
    if (mode === "jobs") {
      return {
        id: `json-${i}`,
        selected: true,
        url: row.url ? String(row.url) : undefined,
        company: String(row.company ?? "Unknown"),
        role: String(row.role ?? row.title ?? "Untitled role"),
        location: row.location ? String(row.location) : undefined,
        notes: row.notes ? String(row.notes) : undefined,
      };
    }
    return {
      id: `json-${i}`,
      selected: true,
      name: String(row.name ?? ""),
      company: String(row.companySlug ?? row.company ?? ""),
      email: row.email ? String(row.email) : undefined,
      role: row.role ? String(row.role) : undefined,
      notes: row.notes ? String(row.notes) : undefined,
    };
  }).filter((r) => (mode === "jobs" ? r.company : r.name && r.company));
}

export function IntakePage() {
  const navigate = useNavigate();
  const batchContacts = useBatchCreateContacts();
  const [mode, setMode] = useState<IntakeMode>("jobs");
  const [raw, setRaw] = useState("");
  const [rows, setRows] = useState<IntakeRow[]>([]);
  const [parseError, setParseError] = useState("");
  const [attributionContactId, setAttributionContactId] = useState("");

  const selectedCount = useMemo(
    () => rows.filter((r) => r.selected).length,
    [rows],
  );

  const importMutation = useMutation({
    mutationFn: async () => {
      const selected = rows.filter((r) => r.selected);
      if (mode === "jobs") {
        const jobs: JobImportRow[] = selected.map((r) => ({
          url: r.url ?? "",
          company: r.company,
          role: r.role ?? "Untitled role",
          location: r.location,
          notes: r.notes,
          source: "import",
          sourceDetail: "batch-intake",
          attributionContactId: attributionContactId.trim() || undefined,
        }));
        return importJobs(jobs, {
          attributionContactId: attributionContactId.trim() || undefined,
        });
      }

      const contacts: ContactInput[] = selected.map((r) => ({
        companySlug: r.company,
        name: r.name ?? "",
        email: r.email,
        role: r.role as ContactInput["role"],
        notes: r.notes,
        source: "import",
      }));
      await batchContacts.mutateAsync(contacts);
      return { jobs: [], skipped: 0 };
    },
    onSuccess: () => {
      navigate(mode === "jobs" ? "/jobs" : "/network");
    },
  });

  function handleParse() {
    setParseError("");
    try {
      const trimmed = raw.trim();
      if (!trimmed) {
        setRows([]);
        return;
      }
      const parsed =
        trimmed.startsWith("[") || trimmed.startsWith("{")
          ? parseJson(trimmed, mode)
          : mode === "jobs"
            ? parseJobsCsv(trimmed)
            : parseContactsCsv(trimmed);
      if (parsed.length === 0) {
        setParseError("No valid rows found. Check headers or JSON shape.");
      }
      setRows(parsed);
    } catch {
      setParseError("Could not parse input. Use CSV with headers or a JSON array.");
      setRows([]);
    }
  }

  function toggleRow(id: string) {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, selected: !r.selected } : r)),
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
      <header>
        <Link to="/network" className="text-sm text-brand hover:underline">
          ← Network
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-foreground">Batch intake</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Paste a recruiter job list (CSV/JSON) or contact export, review, then promote to your inbox or CRM.
          For URL enrichment and column mapping, use{" "}
          <Link to="/jobs/add?mode=batch" className="text-brand hover:underline">
            Add Job → Batch CSV
          </Link>
          .
        </p>
      </header>

      <div className="flex gap-2">
        {(["jobs", "contacts"] as IntakeMode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => {
              setMode(m);
              setRows([]);
              setParseError("");
            }}
            className={cn(
              "rounded-md border px-3 py-1.5 text-sm font-medium",
              mode === m
                ? "border-brand bg-brand/10 text-brand"
                : "border-border text-muted-foreground hover:bg-muted/20",
            )}
          >
            {m === "jobs" ? "Job list" : "Contact list"}
          </button>
        ))}
      </div>

      <textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        rows={8}
        placeholder={
          mode === "jobs"
            ? "url,company,role,location\nhttps://…,Acme Corp,Staff Engineer,Remote"
            : "name,company_slug,email,role\nJane Doe,acme,jane@agency.com,recruiter"
        }
        className="w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-xs"
      />

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleParse}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted/20"
        >
          Parse & preview
        </button>
        {mode === "jobs" && (
          <input
            type="text"
            placeholder="Attribution contact ID (optional)"
            value={attributionContactId}
            onChange={(e) => setAttributionContactId(e.target.value)}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
        )}
      </div>

      {parseError && <p className="text-sm text-red-500">{parseError}</p>}

      {rows.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">
            Preview ({selectedCount} selected)
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-muted/20 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Include</th>
                  {mode === "jobs" ? (
                    <>
                      <th className="px-3 py-2">Company</th>
                      <th className="px-3 py-2">Role</th>
                      <th className="px-3 py-2">URL</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Company</th>
                      <th className="px-3 py-2">Email</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={r.selected}
                        onChange={() => toggleRow(r.id)}
                      />
                    </td>
                    {mode === "jobs" ? (
                      <>
                        <td className="px-3 py-2">{r.company}</td>
                        <td className="px-3 py-2">{r.role}</td>
                        <td className="max-w-xs truncate px-3 py-2 text-xs text-muted-foreground">
                          {r.url ?? "—"}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2">{r.name}</td>
                        <td className="px-3 py-2">{r.company}</td>
                        <td className="px-3 py-2">{r.email ?? "—"}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={() => importMutation.mutate()}
            disabled={
              importMutation.isPending ||
              batchContacts.isPending ||
              selectedCount === 0
            }
            className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            {importMutation.isPending || batchContacts.isPending
              ? "Importing…"
              : mode === "jobs"
                ? `Import ${selectedCount} job${selectedCount === 1 ? "" : "s"}`
                : `Import ${selectedCount} contact${selectedCount === 1 ? "" : "s"}`}
          </button>
        </section>
      )}
    </div>
  );
}
